/* eslint-disable css-modules/no-unused-class */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import startsWith from 'lodash/startsWith';
// jl-design-system
import { UnderlinedButton } from 'jl-design-system/elements/button/Button';
import Tooltip from 'jl-design-system/components/tooltip/Tooltip';
// classnames
import classNames from 'classnames';
// helpers
import { shouldShowCollectionHelpText } from '../../utils/collection/collectionHelp';
import { shouldShowPersonalisationHelpText } from '../../utils/personalisation/personalisationHelp';
import {
  getInformationList,
  getSubtotalText,
  getQuantityText,
  getQuantityTextFabricItem,
  getQuantitySelector,
  getReducedQuantityText,
  getReducedQuantityTextForFabricItem,
} from './OrderItemHelper';
import thumbnail from '../../utils/image/thumbnail';
// styles
import styles from './order-item.scss';

export class OrderItem extends PureComponent {

  static propTypes = {
    basketView: PropTypes.bool,
    batchedView: PropTypes.bool,
    child: PropTypes.bool.isRequired,
    displayImage: PropTypes.bool.isRequired,
    hideOutOfStockText: PropTypes.bool,
    hideQuantities: PropTypes.bool,
    hideSubtotals: PropTypes.bool,
    id: PropTypes.string,
    imageUrl: PropTypes.string,
    information: PropTypes.arrayOf(PropTypes.object),
    modal: PropTypes.bool,
    name: PropTypes.string.isRequired,
    partnerDiscount: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    partnerDiscountEnabled: PropTypes.bool,
    preventEditing: PropTypes.bool,
    priceHistory: PropTypes.shape({}),
    quantity: PropTypes.number.isRequired,
    reducedQuantity: PropTypes.number,
    removeItem: PropTypes.func,
    showCollectionHelp: PropTypes.bool,
    showPersonalisationMessage: PropTypes.bool,
    sidebar: PropTypes.bool,
    stockLevel: PropTypes.number,
    subtotal: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
    subtotalDiscounted: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    type: PropTypes.string,
    uom: PropTypes.string,
  };

  static defaultProps = {
    basketView: false,
    batchedView: false,
    child: false,
    hideOutOfStockText: false,
    hideQuantities: false,
    hideSubtotals: false,
    id: '',
    imageUrl: '',
    information: undefined,
    modal: false,
    name: '',
    partnerDiscount: undefined,
    partnerDiscountEnabled: false,
    preventEditing: false,
    priceHistory: undefined,
    reducedQuantity: undefined,
    removeItem: window.defaultFunc,
    showCollectionHelp: false,
    showPersonalisationMessage: false,
    sidebar: false,
    stockLevel: undefined,
    subtotal: '',
    subtotalDiscounted: undefined,
    type: undefined,
    uom: '',
  };

  getContainerClassnames = () => {
    const { basketView, batchedView, child, sidebar, modal } = this.props;

    return classNames(styles.container, {
      [styles.basketView]: basketView,
      [styles.batchedView]: batchedView,
      [styles.containerChild]: child,
      [styles.sidebar]: sidebar,
      [styles.modal]: modal,
    });
  };

  getItemDetailsContainerClassnames = () => {
    const { displayImage } = this.props;

    return classNames(styles.itemDetailsContainer, {
      [styles.imageMarginApplied]: !displayImage,
    });
  };

  render() {
    const {
      batchedView,
      name,
      quantity,
      subtotal,
      subtotalDiscounted,
      imageUrl,
      displayImage,
      uom,
      information,
      partnerDiscount,
      partnerDiscountEnabled,
      sidebar,
      type,
      hideSubtotals,
      hideQuantities,
      reducedQuantity,
      stockLevel,
      hideOutOfStockText,
      id,
      preventEditing,
      priceHistory: {
        reducedToClear,
        originalSubtotal,
      } = {},
      removeItem,
      showCollectionHelp,
      showPersonalisationMessage,
    } = this.props;

    const isItemFabric = uom !== '' && !startsWith(type, 'm2m');

    return (

      <div className={this.getContainerClassnames()} data-test="items">
        {
          displayImage ? (
            <div className={styles.itemImage}>
              <img
                alt={name}
                aria-hidden="true"
                data-test="order-item-image"
                src={thumbnail(imageUrl)}
              />
            </div>
          ) : null
        }

        <div className={this.getItemDetailsContainerClassnames()} data-test="item-details-container">
          <div className={styles.itemType}>
            <p
              className={styles.itemName}
              data-test="order-item-name"
            >
              {name}
            </p>
            {
              showCollectionHelp &&
              <p className={styles.showCollectionHelp}>Collection unavailable</p>
            }
            {
              showPersonalisationMessage &&
              <div className={styles.personalisationTooltip}>
                <Tooltip
                  anchor={sidebar ? 'right' : 'left'}
                  buttonLabel="Includes personalisation"
                  icon
                  id={`personalisationTooltip-${id}`}
                >
                  <p>
                    Once you’ve placed your order, please email your personalisation details to the supplier.
                  </p>
                  <p>
                    Details on how to do this can be found on the product details page or the order confirmation page.
                  </p>
                </Tooltip>
              </div>
            }
            {
              information && !batchedView &&
              getInformationList(information, styles)
            }
          </div>

          {
            reducedQuantity &&
            <React.Fragment>
              {
                !isItemFabric && (preventEditing || reducedQuantity === 1) &&
                <div className={styles.reducedQuantityContainer}>
                  { getReducedQuantityText(quantity, reducedQuantity, styles) }
                </div>
              }
              {
                isItemFabric && (preventEditing || reducedQuantity === 1) &&
                <div className={styles.reducedQuantityContainer}>
                  { getReducedQuantityTextForFabricItem(quantity, reducedQuantity, styles) }
                </div>
              }
              {
                !preventEditing && reducedQuantity > 1 &&
                getQuantitySelector({
                  id,
                  type,
                  uom,
                  availability: {
                    stockLevel,
                  },
                }, reducedQuantity)
              }
            </React.Fragment>
          }

          {
            !reducedQuantity &&
            !hideQuantities &&
            <React.Fragment>
              {
                isItemFabric &&
                getQuantityTextFabricItem(quantity, styles)
              }
              {
                !isItemFabric &&
                getQuantityText(quantity, styles)
              }
            </React.Fragment>
          }

          {
            !hideSubtotals && !batchedView &&
            getSubtotalText(
              partnerDiscount,
              partnerDiscountEnabled,
              sidebar,
              subtotal,
              subtotalDiscounted,
              styles,
              reducedToClear,
              originalSubtotal,
            )
          }

          {
            (!hideOutOfStockText && stockLevel === 0) &&
            <p className={styles.outOfStock}>
              Sorry, this item is no longer available.
              Please <UnderlinedButton
                onClick={removeItem}
              >remove</UnderlinedButton> this item to continue.
            </p>
          }
        </div>
      </div>
    );
  }

}

export default connect((state, ownProps) => ({
  showCollectionHelp: shouldShowCollectionHelpText({ isChildItem: ownProps.child, id: ownProps.id, state }),
  showPersonalisationMessage: shouldShowPersonalisationHelpText({
    isChildItem: ownProps.child,
    personalised: ownProps.personalised,
    state,
  }),
}))(OrderItem);
